import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import { FormattedMessage } from 'react-intl';
import { withSiteData, withRouteData } from 'react-static';

import Hyperlink from '../../common/Hyperlink';
import { media } from '../../../styles/style-utils';
import { type } from '../../../styles/typography';
import { COUNTRY_CODE_COOKIE, GIFT_CARD_PATH } from '../../../constants';

const LINKS_OFFSET = 500;

const Wrapper = styled.footer`
  background-color: var(--b-black);
  color: var(--b-white);

  .inapp-webview &,
  .ios-webview & {
    display: none !important;
  }
`;

const Columns = styled.nav`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px 20px;
  padding: 32px var(--b-gutter) 80px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.13);

  ${media.large`
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    padding-left: ${LINKS_OFFSET}px;
  `};

  ul {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
  }

  li {
    list-style: none;
  }

  a {
    ${type('detail', 'medium', { emphasis: true })};
    color: var(--b-white);
    text-decoration: none;
    transition: color 0.16s ease;

    &:hover {
      color: rgba(255, 255, 255, 0.72);
    }
  }
`;

const Legal = styled.div`
  ${type('detail', 'xsmall')};
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 44px;
  padding: 14px var(--b-gutter);
  color: rgba(255, 255, 255, 0.56);

  ${media.large`
    display: grid;
    grid-template-columns: ${LINKS_OFFSET - 20}px repeat(4, 1fr);
    align-items: center;
    gap: 0;
  `};

  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 20px;
    margin: 0;
    padding: 0;

    ${media.large`
      display: contents;
    `};
  }

  li {
    list-style: none;
    white-space: nowrap;
  }

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: var(--b-white);
    }
  }
`;

const SOCIAL_LINKS = [
  { label: 'Instagram', link: 'https://www.instagram.com/idagioofficial/' },
  { label: 'Facebook', link: 'https://www.facebook.com/idagioofficial' },
  { label: 'Youtube', link: 'https://www.youtube.com/c/IDAGIOclassical' },
  { label: 'Twitter / X', link: 'https://twitter.com/idagio_official' },
];

class Footer extends Component {
  state = { isInGermany: false };

  componentDidMount() {
    if (Cookies.get(COUNTRY_CODE_COOKIE) === 'DE') {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ isInGermany: true });
    }
  }

  render() {
    const { appDomain, language } = this.props;
    const { isInGermany } = this.state;

    return (
      <Wrapper>
        <Columns aria-label="Footer">
          <ul>
            <li>
              <Hyperlink to="/about">
                <FormattedMessage id="header.cta.about" defaultMessage="About" />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to="/magazine">
                <FormattedMessage id="header.cta.magazine" defaultMessage="Magazine" />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to={`https://support.idagio.com/${language}`} openInNewTab>
                <FormattedMessage id="header.cta.support" defaultMessage="Support" />
              </Hyperlink>
            </li>
            {isInGermany && (
              <li>
                <Hyperlink to={`${appDomain}/account`} openInNewTab>
                  <FormattedMessage
                    id="front-page.footer.cancel-subs"
                    defaultMessage="Cancel subscription"
                  />
                </Hyperlink>
              </li>
            )}
          </ul>
          <ul>
            <li>
              <Hyperlink to={`/${GIFT_CARD_PATH}`}>
                <FormattedMessage id="header.cta.gift-cards" defaultMessage="Gift Cards" />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to="https://store.idagio.com" openInNewTab>
                <FormattedMessage id="header.cta.store" defaultMessage="Store" />
              </Hyperlink>
            </li>
          </ul>
          <ul>
            <li>
              <Hyperlink to="/jobs">
                <FormattedMessage id="footer.links.jobs" defaultMessage="Jobs" />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to="/press">
                <FormattedMessage id="front-page.footer.press" defaultMessage="Press" />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to="/partner">
                <FormattedMessage id="header.cta.partner" defaultMessage="Partners" />
              </Hyperlink>
            </li>
          </ul>
          <ul>
            {SOCIAL_LINKS.map(({ label, link }) => (
              <li key={label}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </Columns>

        <Legal>
          <div>&copy; IDAGIO {new Date().getFullYear()} &middot; Made in Berlin</div>
          <ul>
            <li>
              <Hyperlink to={`${appDomain}/consent-manager`} openInNewTab>
                <FormattedMessage
                  id="front-page.footer.cookie-policy"
                  defaultMessage="Cookies Policy"
                />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to="/privacy">
                <FormattedMessage id="front-page.footer.privacy" defaultMessage="Privacy Policy" />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to="/terms">
                <FormattedMessage
                  id="front-page.footer.terms"
                  defaultMessage="Terms & Conditions"
                />
              </Hyperlink>
            </li>
            <li>
              <Hyperlink to="/imprint">
                <FormattedMessage id="front-page.footer.imprint" defaultMessage="Imprint" />
              </Hyperlink>
            </li>
          </ul>
        </Legal>
      </Wrapper>
    );
  }
}

Footer.propTypes = {
  appDomain: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
};

export default withSiteData(withRouteData(Footer));



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/main/footer/Footer.js